/**
 * Order review modal for approve/reject workflow.
 * @module ReviewModal
 */

import { ChangeEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "risa-oasis-ui_v2";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { ModalId } from "../../../enums/modalId";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import {
  decrementReviewPendingCount,
  decrementReviewPendingCountByKey,
} from "../../../redux/slice/medicalPa/reviewPendingCountSlice";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import WarningIcon from "../../../svg/warningIcon";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import DragDropUpload from "../../dragDropUpload/dragDropUpload";
import { handleSubmit } from "./actionHandlers/handleSubmit";

/** Props for the {@link ReviewModal} component. */
interface ReviewModalProps {
  /** Callback to close the modal. */
  onClose: () => void;
  /** PA order ID under review. */
  orderId: string;
  orgId: string;
  patientId: string;
  /** Email or name of the reviewing user. */
  reviewedBy: string;
  /** Order's master_auth_status; when provided, decrements that tab's review-pending count. */
  masterAuthStatus?: string;
  /** Optional callback invoked after a successful review submission. */
  onSubmit?: () => void;
}

/**
 * Modal for approving or rejecting a medical PA order review.
 * Allows the user to select approve/reject, enter comments, and optionally
 * upload a PDF. On submit, persists the review and adjusts the review-pending
 * count in Redux before navigating back to the worklist.
 *
 * @param props - {@link ReviewModalProps}
 */
const ReviewModal = (props: ReviewModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchAll } = useDataAccumulatorForSingleOrder();
  const [selectedAction, setSelectedAction] = useState<"approve" | "reject">(
    "reject",
  );
  const [comments, setComments] = useState<string>("");
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const filePath = `medical_pa_order/${props.orderId}/order_review_doc/review_doc.pdf`;

  const handleClose = () => {
    setSelectedAction("reject");
    setComments("");
    setUploadedFileUrls([]);
    props.onClose();
  };

  /**
   * Submits the review, refreshes the order data, and returns to its worklist.
   *
   * @returns A promise that resolves after the review workflow completes.
   */
  const onSubmitHandler = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await handleSubmit({
        orderId: props.orderId,
        orgId: props.orgId,
        patientId: props.patientId,
        reviewedBy: props.reviewedBy,
        selectedAction,
        comments,
        uploadedFileUrls,
        onSubmit: props.onSubmit,
        onClose: handleClose,
      });
      await fetchAll(props.orderId, props.orgId, { force: true });
      if (props.masterAuthStatus) {
        dispatch(decrementReviewPendingCountByKey(props.masterAuthStatus));
      } else {
        dispatch(decrementReviewPendingCount());
      }
      const parentTable = UserStore.getItem(
        UserGeneralInfoContextKeys.PARENT_TABLE,
      );
      navigate(`/worklists/${parentTable || "medical-pa-orders"}`);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUrlsChange = (urls: string[]) => {
    setUploadedFileUrls(urls.filter((url) => url !== ""));
  };

  const isFormValid =
    selectedAction === "approve" || comments.trim().length > 0;

  return (
    <Modal
      dialogId={ModalId.REVIEW_MODAL}
      title="Review"
      onClose={handleClose}
      onCancel={handleClose}
      onSave={onSubmitHandler}
      saveButtonText={isLoading ? "Submitting..." : "Submit"}
      cancelText="Cancel"
      disableSave={!isFormValid || isLoading}
      hideFooter={false}
    >
      <div className="flex flex-col gap-4 px-0 py-2">
        <div className="flex items-center gap-2 rounded bg-amber-50 px-3 py-2">
          <WarningIcon height="16" width="18" />
          <span className="text-smaller font-normal text-amber-700">
            Review Caution: Please ensure the Auth Status, Notes, BO Values, and
            Documents are corrected in OncoEMR before submitting the review.
            Reviews should be provided only after the order is fully corrected.
          </span>
        </div>
        <div className="flex flex-col gap-3">
          <div className="text-smaller font-normal text-primaryGray-2">
            What would you mark this order as?
          </div>
          <div className="flex flex-col gap-2">
            <CustomRadioButton
              name="review-action"
              groupName="review-action"
              value="approve"
              checked={selectedAction === "approve"}
              label="Approve"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  setSelectedAction("approve");
                }
              }}
            />
            <CustomRadioButton
              name="review-action"
              groupName="review-action"
              value="reject"
              checked={selectedAction === "reject"}
              label="Reject"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  setSelectedAction("reject");
                }
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-tiny font-normal text-primaryGray-2">
            Comments
          </div>
          <textarea
            placeholder="Enter your comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className="h-[6.75rem] w-full resize-none rounded border border-primaryGray-14 p-3 text-small placeholder-gray-400 focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-2">
          <DragDropUpload
            id="review-file-upload"
            allowedFileTypes={[".pdf"]}
            maxFileSize={5 * 1024 * 1024}
            multipleFilesAllowed={false}
            subText="Supported file format .pdf not exceeding 5MB"
            filePath={filePath}
            fileListLength={(length) => {}}
            downloadedURL={handleFileUrlsChange}
            onDeleteFile={() => {
              setUploadedFileUrls([]);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReviewModal;
